
import 'keen-slider/keen-slider.min.css';
import KeenSlider from 'keen-slider';
import respUtility from '@/mixins/respUtility';

export default {
  name: 'ProductInfoGallerySliders',
  mixins: [respUtility],
  props: {
    mainGallery: {
      type: Array,
      default() {
        return [];
      },
    },
    thumbGallery: {
      type: Array,
      default() {
        return [];
      },
    },
    view3D: {
      type: Object,
      default() {
        return {};
      },
    },
    webId: {
      type: String,
      default: '',
    },
    getBtn3D: {
      type: Object,
      default() {
        return null;
      },
    },
    setQrCode: {
      type: Function,
      default: null,
    },
    isGalleryPopup: {
      type: Boolean,
      default: false,
    },
    isTabs: {
      type: Boolean,
      default: false,
    },
    indexSliderPopup: {
      type: Number,
    },
    galleriesCount: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      slider: null,
      thumbnail: null,
      current: this.indexSliderPopup || 0,
      currentAnimationEnded: this.indexSliderPopup || 0,
    };
  },
  watch: {
    'view3D.done': function getNewValue(newValue) {
      if (newValue) {
        this.$taskManager.append(() => {
          if (this.slider || this.thumbnail) this.initSliders();
        });
      }
    },
    currentAnimationEnded: function updateSimilarSrc() {
      this.$emit('update-similar-src', this.getSimilarSrc);
    },
  },
  computed: {
    getSimilarSrc() {
      // eslint-disable-next-line max-len, vue/max-len
      const src = this.mainGallery[this.currentAnimationEnded]?.__typename === `${this.$pdpConstants.PRODUCT_IMAGE_TYPE}`
        ? this.mainGallery[this.currentAnimationEnded]?.src : null;
      this.$emit('update-similar-src', src);
      return src;
    },
    dotHelper() {
      return this.slider ? [...Array(this.slider.track.details.slides.length).keys()] : [];
    },
    centeredSlider() {
      const screenWidth = process.client ? window.innerWidth : 0;
      return (screenWidth >= 1600 && this.thumbGallery?.length < 8)
            || (screenWidth >= 1440 && this.thumbGallery?.length < 7)
            || (screenWidth >= 1264 && this.thumbGallery?.length < 6);
    },
    showThumbGallery() {
      return this.thumbGallery?.length > 1;
    },
    showThumbGalleryPDP() {
      return !this.isGalleryPopup
        && !(this.$device.isMobileOrTablet || this.isTouchDevice);
    },
    showThumbGalleryPopup() {
      return this.isGalleryPopup;
    },
    isBoutiquePage() {
      return this.$store.state.metaData?.addons?.head?.isBoutique || false;
    },
  },
  mounted() {
    this.$taskManager.append(() => {
      if (this.$isSpider) {
        return;
      }
      this.initSliders();
      document.addEventListener('keydown', this.handleArrows);
    });
    this.$taskManager.append(() => {
      this.$emit('update-similar-src', this.getSimilarSrc);
    });
  },
  beforeDestroy() {
    if (this.slider) this.slider.destroy();
    if (this.thumbnail) this.thumbnail.destroy();
    document.removeEventListener('keydown', this.handleArrows);
  },
  methods: {
    thumbnailPlugin(main) {
      return (slider) => {
        let currentIdx = this.current;
        let stopAnimate = false;

        function removeActive() {
          slider.slides.forEach((slide) => {
            slide.classList.remove('active');
          });
        }

        function addActive(idx) {
          slider.slides[idx].classList.add('active');
        }

        function addClickEvents() {
          slider.slides.forEach((slide, idx) => {
            slide.addEventListener('click', () => {
              currentIdx = idx;
              main.moveToIdx(idx);
            });
          });
        }

        function addHoverEvents() {
          slider?.slides?.forEach((slide, idx) => {
            slide.addEventListener('mouseenter', () => {
              stopAnimate = true;
              main.moveToIdx(idx);
            });
          });
          slider?.container?.addEventListener('mouseleave', () => {
            main.moveToIdx(currentIdx);
            stopAnimate = false;
          });
        }

        slider.on('created', () => {
          addActive(this.current || slider.track.details.rel);
          addClickEvents();
          if (!(this.$device.isMobileOrTablet || this.isTouchDevice)) {
            addHoverEvents();
          }
          // eslint-disable-next-line no-shadow
          main.on('animationStarted', (main) => {
            removeActive();
            const next = main.animator.targetIdx || 0;
            addActive(main.track.absToRel(next));
            if (stopAnimate) {
              slider.moveToIdx(Math.min(slider.track.details.maxIdx, next, { duration: 0 }));
            } else {
              slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
              currentIdx = main.track.absToRel(next);
            }
          });
        });
      };
    },
    initSliders() {
      if (this.mainGallery?.length > 1) {
        this.slider = new KeenSlider(
          this.$refs.mainSlider,
          {
            initial: this.current,
            animationEnded: (s) => {
              this.currentAnimationEnded = s.track.details.rel;
              this.current = s.track.details.rel;
              if (this.$device.isMobileOrTablet || this.isTouchDevice) {
                this.centeredThumb(this.current);
              }
            },
          },
        );
      }
      if (this.showThumbGallery && (this.showThumbGalleryPDP || this.showThumbGalleryPopup)) {
        this.thumbnail = new KeenSlider(
          this.$refs.thumbnailSlider,
          {
            initial: this.current,
            disabled: true,
            defaultAnimation: {
              duration: 0,
            },
            slides: {
              perView: 'auto',
            },
            breakpoints: !this.isGalleryPopup ? {
              '(min-width: 1024px)': {
                disabled: false,
                slides: {
                  perView: 4,
                  spacing: 12,
                },
              },
              '(min-width: 1264px)': {
                disabled: false,
                slides: {
                  perView: 6,
                  spacing: 12,
                },
              },
              '(min-width: 1440px)': {
                disabled: false,
                slides: {
                  perView: 7,
                  spacing: 12,
                },
              },
              '(min-width: 1600px)': {
                disabled: false,
                slides: {
                  perView: 8,
                  spacing: 12,
                },
              },
            } : {},
            created: () => {
              if (this.$device.isMobileOrTablet || this.isTouchDevice) {
                setTimeout(() => {
                  this.centeredThumb(this.current);
                }, 300);
              }
            },
          },
          [this.thumbnailPlugin(this.slider)],
        );
      }
    },
    centeredThumb(index) {
      let container = null;
      if (this.$refs.thumbnailSlider) {
        container = this.$refs.thumbnailSlider;
      } else if (this.$refs.mainSliderDots) {
        container = this.$refs.mainSliderDots;
      }

      const containerRect = container.getBoundingClientRect();

      let targetRect = null;
      if (this.$refs.thumbnailSlider) {
        targetRect = this.thumbnail?.slides[index].getBoundingClientRect();
      } else if (this.$refs.mainSliderDots) {
        targetRect = this.$refs.mainSliderDot[index].getBoundingClientRect();
      }

      const targetCenter = targetRect.left + targetRect.width / 2;
      const containerCenter = containerRect.left + containerRect.width / 2;

      const scrollOffset = targetCenter - containerCenter;

      container.scrollTo({
        left: container.scrollLeft + scrollOffset,
        behavior: 'smooth',
      });
    },
    openPopup(index, tab) {
      this.$emit('open-popup', index, tab);
    },
    handleArrows(event) {
      if (
        event.key === 'ArrowRight' && event.keyCode === 39
        && this.current !== this.slider.track.details.slides.length - 1
      ) {
        this.slider.next();
      }
      if (
        event.key === 'ArrowLeft' && event.keyCode === 37
        && this.current !== 0
      ) {
        this.slider.prev();
      }
    },
  },
};
